/**
 * What a pasted document MEANS, rather than how it was drawn.
 *
 * WORD PROCESSORS DRAW HEADINGS WITH TABLES. The rule under "DESCRIPTION" in a controlled document
 * is almost never a heading style with a border -- it is a one-cell table with a bottom edge,
 * because that is what survives thirty years of Word versions. Pasting an Instructions For Use into
 * this editor produced twenty tables and ZERO headings: every section title was a table cell.
 *
 * IT LOOKED ALMOST RIGHT, WHICH IS THE PROBLEM. The lines were there, the text was there, and the
 * document had no structure at all -- nothing to build an outline from, nothing for a renderer to
 * make a table of contents out of, and nothing for the submission checks to reason about. A
 * procedure whose sections are table cells is a picture of a procedure.
 *
 * SO LAYOUT TABLES ARE UNWRAPPED AND DATA TABLES ARE NOT, and the line between them is the number
 * of rows:
 *
 *   * ONE ROW, ONE CELL, one short paragraph  -> a heading. Nobody tabulates a single value.
 *   * ONE ROW, ONE CELL, anything else        -> unwrapped; the cell was a box drawn round content.
 *   * EVERYTHING ELSE                         -> kept, exactly as it arrived.
 *
 * A MULTI-COLUMN BLOCK IS KEPT, AND IT DID NOT USED TO BE. One row of several cells is the
 * two-column list every Instructions For Use puts its contraindications in, and this flattened it
 * into one list in reading order -- arguing that as a table it cannot be reordered, renumbered or
 * read aloud in sequence. All true, and beside the point: the two columns are a decision somebody
 * made about how the page reads, and the half of this transform that is worth having is the half
 * that recovers STRUCTURE THAT WAS NEVER THERE -- a heading drawn as a table. Changing a layout the
 * author chose is a different act, and not one a paste should perform silently.
 *
 * So the rule is now the narrow one: a single cell is a box around something, and anything with
 * more than one cell is a layout, and layouts are left alone.
 *
 * APPLIED ON PASTE, NOT ON LOAD. A body already stored this way is what it is; rewriting documents
 * at read time would mean the text on screen is not the text that was signed.
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Fragment, Node as PMNode, Slice } from "@tiptap/pm/model";

/** Longer than this and a lone cell is a paragraph in a box, not a title. */
const HEADING_CHARS = 100;

function isTable(node: PMNode): boolean {
    return node.type.name === "table";
}

/** The blocks inside a cell, flattened out of it. */
function cellContent(cell: PMNode): PMNode[] {
    const out: PMNode[] = [];
    cell.forEach(child => out.push(child));
    return out;
}

/**
 * A heading's text, with the source's inline sizing taken off it.
 *
 * THE NODE WAS A HEADING AND STILL LOOKED LIKE BODY TEXT. Promoting the one-cell table gives a real
 * heading node, but the words inside it still carried the source's own `font-size: 9pt` and `bold`
 * as inline marks -- and an inline size beats the stylesheet, so twelve headings rendered as small
 * bold paragraphs. Structurally right, visually indistinguishable from what it replaced.
 *
 * WE DECIDED IT IS A HEADING, so the source's statement about how big it was is superseded: the
 * page's own typography applies. Colour and highlight are left alone -- those carry meaning a
 * writer chose, where a point size on a title is just how the old document drew one.
 *
 * ONLY ON THE PROMOTED NODE. Body text keeps every mark it arrived with, because that is what
 * "paste keeps formatting" means everywhere else.
 */
function headingText(fragment: Fragment, schema: any): Fragment {
    const style = schema.marks.textStyle;
    const bold = schema.marks.bold;
    const out: PMNode[] = [];
    fragment.forEach(node => {
        let marks = node.marks;
        if (style) {
            marks = marks.filter(m => m.type !== style
                || !(m.attrs?.fontSize || m.attrs?.fontFamily));
        }
        if (bold) marks = marks.filter(m => m.type !== bold);
        out.push(node.mark(marks));
    });
    return Fragment.fromArray(out);
}


function unwrap(table: PMNode, schema: any): PMNode[] {
    const rows: PMNode[] = [];
    table.forEach(row => rows.push(row));
    if (rows.length !== 1) return [table];                     // real data; leave it alone

    const cells: PMNode[] = [];
    rows[0].forEach(cell => cells.push(cell));

    if (cells.length === 1) {
        const blocks = cellContent(cells[0]);
        const only = blocks.length === 1 ? blocks[0] : null;
        if (only && only.type.name === "paragraph"
            && only.textContent.trim().length > 0
            && only.textContent.trim().length <= HEADING_CHARS
            && schema.nodes.heading) {
            // THE RULE UNDER IT IS NOT CARRIED ACROSS, deliberately: the line was how the source
            // drew a heading, and here a heading is drawn by being a heading. Keeping both would
            // give every section a border the page's own stylesheet did not ask for.
            return [schema.nodes.heading.create({ level: 1 },
                                               headingText(only.content, schema), only.marks)];
        }
        return blocks;
    }

    // SEVERAL CELLS IS A LAYOUT THE AUTHOR CHOSE. Left exactly as it arrived -- see the note at
    // the top of this file for why this stopped being flattened.
    return [table];
}

function transform(fragment: Fragment, schema: any): Fragment {
    const out: PMNode[] = [];
    fragment.forEach(node => {
        if (isTable(node)) {
            out.push(...unwrap(node, schema).map(n => n.type.name === "table"
                ? n
                : n.copy(transform(n.content, schema))));
            return;
        }
        out.push(node.copy(transform(node.content, schema)));
    });
    return Fragment.fromArray(out);
}

export const pasteStructureKey = new PluginKey("parkourPasteStructure");

export const PasteStructure = Extension.create({
    name: "parkourPasteStructure",
    addProseMirrorPlugins() {
        const schema = this.editor.schema;
        return [
            new Plugin({
                key: pasteStructureKey,
                props: {
                    transformPasted(slice) {
                        // openStart/openEnd are preserved so pasting INTO a paragraph still merges
                        // at the edges; only the node types in between are rewritten.
                        return new Slice(transform(slice.content, schema),
                                         slice.openStart, slice.openEnd);
                    },
                },
            }),
        ];
    },
});
